import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcCafe } from './CalccafeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './CapitalCafe.css';

// Funções de formatação de valores
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
};

const formatNumberForDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(String(value).replace(/\D/g, ''));
    if (isNaN(number)) return '';
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(number);
};

const CapitalCafe = () => {
  const { dados, setDados } = useCalcCafe();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);

  useEffect(() => {
    if (!dados.capital) {
      setDados(prev => ({
        ...prev,
        capital: {
          numeroPesPlantados: '', custoPorPe: '', precoHectareTerraNua: '',
          investimentoBenfeitoriasMaquinasEquipamentos: '', remuneracaoCapitalInvestido: '', taxaDepreciacaoAnual: ''
        }
      }));
    }
    if (dados.resultadosCapital) {
      setMostrarResultados(true);
    }
  }, [dados.capital, dados.resultadosCapital, setDados]);

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({ ...prev, capital: { ...prev.capital, [name]: rawValue } }));
  };
  
  const handleIntegerChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({ ...prev, capital: { ...prev.capital, [name]: rawValue } }));
  }

  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/[^0-9,.]/g, '').replace(',', '.');
    setDados(prev => ({ ...prev, capital: { ...prev.capital, [name]: rawValue } }));
  };

  const handleCalcular = () => {
    const producaoTotalSacas = (Number(dados.producao?.producaoTotal) || 0) / 100;
    const areaPlantadaHectares = (Number(dados.producao?.areaPlantada) || 0) / 100;
    if (producaoTotalSacas === 0 || areaPlantadaHectares === 0) {
      alert("Dados da tela de 'Produção' (Produção Total e Área Plantada) são necessários e devem ser maiores que zero.");
      return;
    }
    
    const c = dados.capital;
    const numeroPesPlantados = Number(c.numeroPesPlantados) || 0;
    const custoPorPe = (Number(c.custoPorPe) || 0) / 100;
    const precoHectareTerraNua = (Number(c.precoHectareTerraNua) || 0) / 100;
    const investimentoBenfeitorias = (Number(c.investimentoBenfeitoriasMaquinasEquipamentos) || 0) / 100;
    const remuneracaoCapital = (parseFloat(c.remuneracaoCapitalInvestido) || 0) / 100;
    const taxaDepreciacao = (parseFloat(c.taxaDepreciacaoAnual) || 0) / 100;

    const capitalInvestidoEmTerra = precoHectareTerraNua * areaPlantadaHectares;
    const capitalInvestidoFormacaoCafe = numeroPesPlantados * custoPorPe;
    const pesPorHectare = areaPlantadaHectares > 0 ? numeroPesPlantados / areaPlantadaHectares : 0;
    const capitalTotalInvestido = investimentoBenfeitorias + capitalInvestidoEmTerra + capitalInvestidoFormacaoCafe;
    const rentabilidadeEsperadaCapital = remuneracaoCapital * capitalTotalInvestido;
    const depreciacao = taxaDepreciacao * (investimentoBenfeitorias + capitalInvestidoFormacaoCafe);
    const rentabilidadePorSaca = rentabilidadeEsperadaCapital / producaoTotalSacas;
    const depreciacaoPorSaca = depreciacao / producaoTotalSacas;

    const resultados = {
      capitalInvestidoEmTerra: capitalInvestidoEmTerra.toFixed(2),
      capitalInvestidoFormacaoCafe: capitalInvestidoFormacaoCafe.toFixed(2),
      pesPorHectare: pesPorHectare.toFixed(2),
      capitalTotalInvestido: capitalTotalInvestido.toFixed(2),
      rentabilidadeEsperadaCapital: rentabilidadeEsperadaCapital.toFixed(2),
      depreciacao: depreciacao.toFixed(2),
      rentabilidadePorSaca: rentabilidadePorSaca.toFixed(2),
      depreciacaoPorSaca: depreciacaoPorSaca.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosCapital: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatNumberResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleDownloadPDF = () => { /* Adicione sua lógica de PDF aqui quando precisar */ };
  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
        setDados(prev => {
            const { capital, resultadosCapital, ...resto } = prev;
            localStorage.setItem('calcCafeData', JSON.stringify(resto));
            return resto;
        });
        setMostrarResultados(false);
    }
  };

  return (
    <div className="cafe-capital-container">
      <div className="cafe-capital-card">
        <h1>Capital Investido - Café</h1>
        <p className="cafe-capital-subtitle">Preencha os campos para calcular os indicadores.</p>

        <div className="cafe-capital-form-results-grid">
            <div className="cafe-capital-form">
                <div className="cafe-capital-input-group">
                    <label>Nº de Pés Plantados</label>
                    <input type="text" name="numeroPesPlantados" placeholder="Ex: 5.000" value={formatNumberForDisplay(dados.capital?.numeroPesPlantados)} onChange={handleIntegerChange} />
                </div>
                <div className="cafe-capital-input-group">
                    <label>Custo por Pé (R$)</label>
                    <input type="text" name="custoPorPe" placeholder="Ex: 15,00" value={formatCurrencyForDisplay(dados.capital?.custoPorPe)} onChange={handleCurrencyChange} />
                </div>
                <div className="cafe-capital-input-group">
                    <label>Preço Hectare Terra Nua (R$)</label>
                    <input type="text" name="precoHectareTerraNua" placeholder="Ex: 40.000,00" value={formatCurrencyForDisplay(dados.capital?.precoHectareTerraNua)} onChange={handleCurrencyChange} />
                </div>
                <div className="cafe-capital-input-group">
                    <label>Capital em Benfeitorias, Máquinas e Equip. (R$)</label>
                    <input type="text" name="investimentoBenfeitoriasMaquinasEquipamentos" placeholder="Ex: 150.000,00" value={formatCurrencyForDisplay(dados.capital?.investimentoBenfeitoriasMaquinasEquipamentos)} onChange={handleCurrencyChange} />
                </div>
                <div className="cafe-capital-input-group">
                    <label>Remuneração do Capital Investido (%)</label>
                    <input type="text" name="remuneracaoCapitalInvestido" placeholder="Ex: 1,00" value={dados.capital?.remuneracaoCapitalInvestido} onChange={handlePercentageChange} />
                </div>
                <div className="cafe-capital-input-group">
                    <label>Taxa de Depreciação Anual (%)</label>
                    <input type="text" name="taxaDepreciacaoAnual" placeholder="Ex: 10,00" value={dados.capital?.taxaDepreciacaoAnual} onChange={handlePercentageChange} />
                </div>
                <button className="cafe-capital-button" onClick={handleCalcular}>Calcular</button>
            </div>

            {mostrarResultados && (
              <div className="cafe-capital-results">
                <h2>Resultados</h2>
                <ul>
                  <li>Capital Investido em Terra: <strong>{formatCurrencyResult(dados.resultadosCapital?.capitalInvestidoEmTerra)}</strong></li>
                  <li>Capital na Formação do Café: <strong>{formatCurrencyResult(dados.resultadosCapital?.capitalInvestidoFormacaoCafe)}</strong></li>
                  <li>Pés por Hectare: <strong>{formatNumberResult(dados.resultadosCapital?.pesPorHectare)}</strong></li>
                  <li>Capital Total Investido: <strong>{formatCurrencyResult(dados.resultadosCapital?.capitalTotalInvestido)}</strong></li>
                  <li>Rentabilidade Esperada do Capital: <strong>{formatCurrencyResult(dados.resultadosCapital?.rentabilidadeEsperadaCapital)}</strong></li>
                  <li>Depreciação: <strong>{formatCurrencyResult(dados.resultadosCapital?.depreciacao)}</strong></li>
                  <li>Rentabilidade por Saca: <strong>{formatCurrencyResult(dados.resultadosCapital?.rentabilidadePorSaca)}</strong></li>
                  <li>Depreciação por Saca: <strong>{formatCurrencyResult(dados.resultadosCapital?.depreciacaoPorSaca)}</strong></li>
                </ul>
                <button className="cafe-capital-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
              </div>
            )}
        </div>
        
        <button className="cafe-capital-help-button" onClick={() => setMostrarAjuda(true)}>Precisa de ajuda?</button>

        <div className="cafe-capital-navigation">
            <button className="cafe-capital-button back" onClick={() => navigate('/calccafe/app/producaoCafe')}>Voltar para Produção</button>
            <button className="cafe-capital-button clean" onClick={handleLimparDados}>Limpar Dados</button>
            <button className="cafe-capital-button navigate" onClick={() => navigate('/calccafe/app/custosCafe')}>Avançar para Custos</button>
        </div>
      </div>

      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
            <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
                <h2>Ajuda - Capital Investido (Café)</h2>
                <p><strong>Custo Capital Investido:</strong> É o que o produtor deixa de ganhar se vendesse todo seu patrimônio e aplicasse no mercado financeiro, em vez de investir na lavoura.</p>
                <p><strong>Dica:</strong> A taxa de remuneração do capital anual pode ser a taxa SELIC, por exemplo. A taxa de depreciação anual para máquinas e lavouras geralmente é de 10%.</p>
                <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default CapitalCafe;

